import {t} from "@lingui/core/macro";
import * as React from "react";

import type {AnalyticsEvent} from "@khanacademy/event-schemas";
import BirthdayPicker from "@khanacademy/wonder-blocks-birthday-picker";
import {View} from "@khanacademy/wonder-blocks-core";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";

type BirthdayPickerProps = JSX.LibraryManagedAttributes<
    typeof BirthdayPicker,
    React.ComponentProps<typeof BirthdayPicker>
>;

interface OwnProps {
    // Text to display as the label for the BirthdayPicker.
    label?: string;
    // Whether entering a birthday is required. Will display a red asterisk.
    // NOTE: This does not actually enforce the requirement, it's just a visual
    // indicator.
    required?: boolean;
    // CEDAR event to fire when the BirthdayPicker is changed. Note that this
    // only fires when the date is valid. So if the user selects month first,
    // then day, the event doesn't fire until the year is selected. But if the
    // component has a default value, the event will fire immediately on change
    // of any dropdown.
    cedarEvent?: AnalyticsEvent;
}

type CombinedProps = OwnProps & BirthdayPickerProps;
type Props = Omit<CombinedProps, "monthYearOnly">;

export default function InnerBirthdayPicker(props: Props): React.ReactElement {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    /**
     * Handle a change to the WB BirthdayPicker component, confirming if the
     * input is valid, and then reporting the result back to the calling
     * component via onChange.
     */
    const handleChange = (dateValue?: string | null) => {
        // Publish the CEDAR event if it exists and the date is valid.
        if (props.cedarEvent && dateValue !== null) {
            publishAnalyticsEvent(props.cedarEvent);
        }

        // NOTE(juan): When `monthYearOnly` is set `true`, this dateValue is
        // passing the LAST day of the month. Make sure to modify it here
        // before passing the value to the callers.
        props.onChange(dateValue);
    };

    const {
        defaultValue,
        disabled,
        labels,
        label,
        required,
        style,
        dropdownStyle,
    } = props;

    return (
        <View>
            <LabeledField
                label={label}
                contextLabel={required ? t`required` : undefined}
                field={
                    <BirthdayPicker
                        defaultValue={defaultValue}
                        disabled={disabled}
                        monthYearOnly={true}
                        onChange={handleChange}
                        labels={
                            labels || {
                                month: t`Month`,
                                day: t`Day`,
                                year: t`Year`,
                                errorMessage: t`Please select a valid birthdate.`,
                            }
                        }
                        locale={RequestInfo.kaLocale}
                        style={style}
                        dropdownStyle={dropdownStyle}
                    />
                }
            />
        </View>
    );
}
